'use client';

import {
  Alert,
  Autocomplete,
  FormControl,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { Regions } from '@/data/inventory/regions';

import { CATALOG_NAME_RE, CatalogNameSection } from './CatalogNameSection';

import type { AddCatalogWizardState } from '../addCatalogWizardState';

interface RegionOption {
  value: string;
  label: string;
}

const AWS_REGION_OPTIONS: RegionOption[] = Object.entries(
  Regions[Provider.Aws]
).map(([code, { displayName }]) => ({
  value: code,
  label: `${code} - ${displayName}`,
}));

export const CatalogSelectRegionStep = (
  props: StepProps<AddCatalogWizardState>
) => {
  const { workspaceRegion, catalogName } = props.wizardState;
  const { region } = props.wizardState;

  const isCrossRegion = region && workspaceRegion && region !== workspaceRegion;

  const canGoNext =
    CATALOG_NAME_RE.test(catalogName) && region.trim().length > 0;

  const selectedRegion = useMemo(
    () => AWS_REGION_OPTIONS.find((o) => o.value === region) ?? null,
    [region]
  );

  return (
    <StepContainer
      stepperIndex={props.stepperLabels.length - 2}
      stepperLabels={props.stepperLabels}
      canGoNext={() => canGoNext}
      nextButtonText='Next'
      backButtonText='Back'
      onBackClick={props.back}
      onNextClick={async () => props.setNextStep(props.currentStep.next?.[0])}
    >
      <Stack gap='32px' padding='24px 40px'>
        <CatalogNameSection
          value={catalogName}
          onChange={(value) =>
            props.setWizardState({
              ...props.wizardState,
              catalogName: value,
            })
          }
        />
        <Stack gap='16px'>
          <Typography variant='subtitle2' fontWeight={700}>
            AWS Region
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Select the AWS region where the Glue Data Catalog resides.
          </Typography>
          <FormControl sx={{ width: '30%' }}>
            <Autocomplete
              options={AWS_REGION_OPTIONS}
              value={selectedRegion}
              onChange={(_event, newValue) =>
                props.setWizardState({
                  ...props.wizardState,
                  region: newValue?.value ?? '',
                })
              }
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size='small'
                  placeholder='Search regions…'
                />
              )}
            />
          </FormControl>
        </Stack>

        {isCrossRegion && (
          <Alert severity='warning'>
            This catalog is in <strong>{region}</strong>, which is different
            from your workspace region <strong>{workspaceRegion}</strong>.
            Cross-region data transfer costs may be incurred.
          </Alert>
        )}
      </Stack>
    </StepContainer>
  );
};
